import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../../config/api';
import { formatPostTimestamp } from '../../utils/dateTime';
import '../../style/card-popular.css';

const GRAD_COUNT = 8;
const CARD_GAP = 12;
const CARD_MIN = 140;
const CARD_MAX = 200;
const CARD_VW = 0.16;
const gradIdx = (id) => (id || 0) % GRAD_COUNT;

function cardWidthForContainer(containerWidth) {
  return Math.min(Math.max(containerWidth * CARD_VW, CARD_MIN), CARD_MAX);
}

/** 뷰포트를 채울 만큼 반복 — 무한 루프의 한 세그먼트(절반)에 필요한 복제 횟수 */
function copiesPerHalf(postCount, containerWidth) {
  const cardW = cardWidthForContainer(containerWidth);
  const minCards = Math.ceil(containerWidth / (cardW + CARD_GAP)) + 2;
  return Math.max(1, Math.ceil(minCards / postCount));
}

function buildLoopSegments(posts, containerWidth) {
  const half = copiesPerHalf(posts.length, containerWidth);
  const segment = Array.from({ length: half }, () => posts).flat();
  return { segment, loopPosts: [...segment, ...segment] };
}

function SlideImage({ post }) {
  const src = post?.featured_image || post?.first_image_url;
  if (!src) return null;
  return (
    <img
      className="popular-slide-img"
      src={API_CONFIG.resolveFileUrl(src)}
      alt={post?.title || ''}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}

function PostSlideCard({ post, onClick }) {
  const displayName = post?.space_id ? post.space_title : post?.author_nickname;

  return (
    <div
      className={`popular-slide card-popular-grad-${gradIdx(post.id)}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    >
      <SlideImage post={post} />
      <div className="popular-slide-gradient" />
      <div className="popular-slide-info">
        {(post.category_name || post.category) && (
          <span className="popular-slide-category">
            {post.category_name || post.category}
          </span>
        )}
        <p className="popular-slide-title">{post.title}</p>
        <div className="popular-slide-meta">
          {displayName}
          {post.timestamp && ` · ${formatPostTimestamp(post.timestamp)}`}
        </div>
        <div className="popular-slide-stats">
          <span className="popular-slide-stat">
            <span className="material-symbols-rounded">web_traffic</span>
            {(post.views || 0).toLocaleString()}
          </span>
          <span className="popular-slide-stat">
            <span className="material-symbols-rounded">favorite</span>
            {(post.likes || 0).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

/** 인기 게시글 캐러셀과 동일한 UI(가로 무한 스크롤 카드 슬라이더)를 재사용하기 위한 공용 컴포넌트 */
function PostCardSlider({ posts, loading = false }) {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const update = () => setContainerWidth(el.offsetWidth);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, posts.length]);

  const { segment, loopPosts } = useMemo(
    () => (posts.length ? buildLoopSegments(posts, containerWidth) : { segment: [], loopPosts: [] }),
    [posts, containerWidth]
  );

  if (posts.length === 0 && !loading) return null;

  return (
    <div className="popular-slider-wrap" ref={wrapRef}>
      {loading ? (
        <div className="popular-slider-skeleton skeleton-pulse" />
      ) : (
        <div className="popular-slider">
          <div
            className="popular-slider-track"
            style={{ '--popular-scroll-duration': `${Math.max(segment.length * 8, 40)}s` }}
          >
            {loopPosts.map((post, i) => (
              <PostSlideCard
                key={`${post.id}-${i}`}
                post={post}
                onClick={() => navigate(`/post?id=${post.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PostCardSlider;
